<template>
    <div class="view">
        <h2>Formulaire de déclaration d'empreinte économique</h2>

        <b-form @submit="onSubmit" @reset="onReset" v-if="show" v-bind:validated="formSubmitted">
            <b-col cols="10">
                <b-row align-h="end" v-if="mode === 'UPDATE'">
                    <b-col cols="3">{{ libelles.dateCreation }} : {{ empreinte.createdDate | formatDate }}</b-col>
                </b-row>
                <b-row align-h="end" class="mb-3">
                    <b-col cols="3"
                    ><span class="typo-orange">* </span>{{ libelles.champsObligatoires }}
                    </b-col>
                </b-row>
                <b-row align-h="between">
                    <b-col cols="4">
                        <b-form-group id="input-group-marcheIds" label-for="input-marcheIds" v-if="isAdmin">
                            <template v-slot:label>
                                <label-custom
                                    :label=libelles.marche
                                    :tooltip=libelles.infoBulleNumMarche
                                    :required="true">
                                </label-custom>
                            </template>
                            <AutocompleteCustom
                                id="input-marcheIds"
                                :items="marcheIdsItems"
                                v-model="marcheAdmin"
                                @search="onMarcheSearch"
                                :required="true"
                                @input="onEntrepriseMarcheChange"
                                :disabled="mode === 'UPDATE' || mode === 'READ'"
                                :placeholder=libelles.selectionnerMarche
                            >
                            </AutocompleteCustom>
                        </b-form-group>

                    </b-col>
                    <b-col cols="6">
                        <div class="user-info">
                            <p style="margin-bottom: 10px; text-align: center;">
                                <strong>Créateur de la saisie</strong>
                            </p>
                            <template v-if="user.nom.trim() || user.prenom.trim() || user.email.trim()">
                                <p><strong>Nom :</strong> {{ user.nom }}</p>
                                <p><strong>Prénom :</strong> {{ user.prenom }}</p>
                                <p><strong>Email :</strong> {{ user.email }}</p>
                            </template>
                            <template v-else>
                                <p class="no-data">La donnée n’est pas disponible</p>
                            </template>
                        </div>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="4">
                        <b-form-group id="input-group-date" label-for="input-date">
                            <template v-slot:label>
                                <label-custom
                                    :label=libelles.donneesMois
                                    :tooltip=empreinteEco.infoBulleDonneesMois
                                    :required="true">
                                </label-custom>
                            </template>
                            <MonthlyPicker
                                id="input-date"
                                :monthLabels="monthLabels"
                                :nb-months-disabled="isAdmin ? false: 3"
                                v-model="date"
                                :clearOption="false"
                                :disabled="mode === 'READ'"
                                v-b-modal="'date-modal'">
                            </MonthlyPicker>
                            <div v-if="date && !isBeforeCurrentDate()">
                                <b-modal id="date-modal"
                                         title="Confirmation choix de la date"
                                         ok-title="Confirmer"
                                         cancel-title="Annuler"
                                         @cancel="date=null">La date est postérieur à la date actuelle. voulez-vous
                                    continuer ?
                                </b-modal>
                            </div>
                            <div class="typo-warning" v-if="!isDateFilled">
                                {{ libelles.champObligatoire }}
                            </div>
                        </b-form-group>
                    </b-col>
                    <b-col cols="6" offset="1" v-if="marcheId && date && mode === 'CREATE'">
                        <b-row>
                            <label-custom
                                :label=libelles.libelleRienDeclarer
                            >
                            </label-custom>
                        </b-row>
                        <b-row class="mt-2">
                            <b-button type="button" variant="primary" v-b-modal="'modal-dispense'">
                                {{ libelles.buttonRienDeclarer }}
                            </b-button>
                            <b-modal
                                :id="'modal-dispense'"
                                :title=libelles.demandeConfirmation
                                ok-title="Confirmer"
                                cancel-title="Annuler"
                                @ok="saveDispense()">
                                <p>{{ libelles.confirmationDispenseUtilisateur }}</p>
                            </b-modal>
                        </b-row>
                    </b-col>
                </b-row>

                <b-card header="Entreprise" class="mb-2">
                    <b-row>
                        <b-col>
                            <b-form-group
                                id="input-group-entrepriseId"
                                label-for="input-entrepriseId">
                                <template v-slot:label>
                                    <label-custom
                                        :label="'Numéro de SIRET complet ' +(marcheId ? '' : ' (veuillez sélectionner une entreprise du référentiel dans le bandeau)')"
                                        :tooltip=empreinteEco.infoBulleEntrepriseId
                                        :required="true">
                                    </label-custom>
                                </template>
                                <AutocompleteCustom
                                    id="input-entrepriseId"
                                    :items="entreprisesSiret"
                                    v-model="entrepriseId"
                                    @search="onSiretLibelleSearch($event, false)"
                                    :required="true"
                                    :formatter="formatSiretSiren"
                                    :disabled="mode === 'UPDATE' || mode === 'READ'"
                                    :extra-option=empreinteEco.libelleNouvelleEntreprise
                                    @extraOptionClicked="onExtraOptionClicked(false)">
                                </AutocompleteCustom>
                                <div class="typo-warning pt-1"
                                     v-if="formSubmitted && !entrepriseId">
                                    {{ empreinteEco.messageEntrepriseId }}
                                </div>
                                <b-button type="button"
                                          variant="outline-primary"
                                          class="mt-1"
                                          v-if="showCreateEntrepriseButton"
                                          @click="onExtraOptionClicked(false)">
                                    Créer une entreprise
                                </b-button>
                            </b-form-group>
                            <b-form-group id="input-group-ess"
                                          label-for="input-ess"
                                          v-if="entreprise">
                                <template v-slot:label>
                                    <label-custom
                                        :label=empreinteEco.libelleEss
                                        :tooltip=empreinteEco.infoBulleEss>
                                    </label-custom>
                                </template>
                                <b-form-radio-group
                                    id="input-ess"
                                    v-model="entreprise.ess"
                                    :buttons="true"
                                    button-variant="outline-primary"
                                    disabled>
                                    <b-form-radio :value="true">Oui</b-form-radio>
                                    <b-form-radio :value="false">Non</b-form-radio>
                                </b-form-radio-group>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-card>
                <b-card header="Qualification du fournisseur" class="mb-2">
                    <b-row>
                        <b-col cols="6">
                            <b-form-group id="input-group-pme" label-for="input-pme">
                                <div v-if="isInfoFournisseur">
                                    <b-alert show variant="primary">
                                        <span v-if="empreinte.entrepriseMarcheInfos.effectif<250">✔</span>
                                        <span v-if="empreinte.entrepriseMarcheInfos.effectif>=250">❌</span>
                                        Effectif inférieur à 250 membres<br>
                                        <span v-if="empreinte.entrepriseMarcheInfos.chiffreAffaire<=50000000">✔</span>
                                        <span v-if="empreinte.entrepriseMarcheInfos.chiffreAffaire>50000000">❌</span>
                                        CA inférieur à 50M€ ou de bilan total inférieur à 43M€<br>
                                        <span v-if="!isPourcentagePlus">✔</span>
                                        <span v-if="isPourcentagePlus">❌</span>
                                        Moins de 25% de participation par une autre entreprise partenaire <br>
                                    </b-alert>
                                </div>
                                <template v-slot:label>
                                    <label-custom
                                        :label=empreinteEco.libellePme
                                        :tooltip=empreinteEco.infoBullePme
                                        :required="true">
                                    </label-custom>
                                </template>
                                <b-form-radio-group
                                    id="input-pme"
                                    v-model="empreinte.entrepriseMarcheInfos.pme"
                                    :buttons="true"
                                    button-variant="outline-primary"
                                    :disabled="pmeInfolegale || isAutonomyPMEDisabled"
                                    required
                                    @change="updateMontantPme($event)">
                                    <b-form-radio :value="true">Oui</b-form-radio>
                                    <b-form-radio :value="false">Non</b-form-radio>
                                </b-form-radio-group>
                            </b-form-group>
                        </b-col>
                        <b-col cols="3">
                            <b-form-group
                                id="input-group-francilienne"
                                label-for="input-francilienne">
                                <template v-slot:label>
                                    <label-custom
                                        :label=empreinteEco.libelleFrancilienne
                                        :tooltip=empreinteEco.infoBulleFrancilienne
                                        :required="true">
                                    </label-custom>
                                </template>
                                <b-form-radio-group
                                    id="input-francilienne"
                                    v-model="empreinte.entrepriseMarcheInfos.francilienne"
                                    :buttons="true"
                                    button-variant="outline-primary"
                                    :disabled="true"
                                    required>
                                    <b-form-radio :value="true">Oui</b-form-radio>
                                    <b-form-radio :value="false">Non</b-form-radio>
                                </b-form-radio-group>
                            </b-form-group>
                        </b-col>
                        <b-col cols="3">
                            <b-form-group
                                id="input-group-agenceIdf"
                                label-for="input-agenceIdf">
                                <template v-slot:label>
                                    <label-custom
                                        :label=empreinteEco.libelleAgenceIdf
                                        :tooltip=empreinteEco.infoBulleAgenceIdf
                                        :required="true">
                                    </label-custom>
                                </template>
                                <b-form-radio-group
                                    id="input-agenceIdf"
                                    v-model="empreinte.entrepriseMarcheInfos.agenceIdf"
                                    :buttons="true"
                                    button-variant="outline-primary"
                                    :disabled="true"
                                    @input="updateQualifFournisseurMDM"
                                    required>
                                    <b-form-radio :value="true">Oui</b-form-radio>
                                    <b-form-radio :value="false">Non</b-form-radio>
                                </b-form-radio-group>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col>
                            <b-form-group
                                id="input-group-chiffreAffaire"
                                label-for="input-chiffreAffaire">
                                <template v-slot:label>
                                    <label-custom
                                        :label=empreinteEco.libelleChiffreAffaire
                                        :tooltip=empreinteEco.infoBulleChiffreAffaire
                                        :required="true">
                                    </label-custom>
                                </template>
                                <b-form-input
                                    id="input-chiffreAffaire"
                                    v-model="empreinte.entrepriseMarcheInfos.chiffreAffaire"
                                    type="number"
                                    min="0"
                                    step="any"
                                    :disabled="isEntrepriseMarcheInfosDisabled || (isDMDInfo && chiffreAffaireInfolegale)"
                                    required>
                                </b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group
                                id="input-group-effectif"
                                label-for="input-effectif">
                                <template v-slot:label>
                                    <label-custom
                                        :label=empreinteEco.libelleEffectif
                                        :tooltip=empreinteEco.infoBulleEffectif
                                        :required="true">
                                    </label-custom>
                                </template>
                                <b-form-input
                                    id="input-effectif"
                                    v-model="empreinte.entrepriseMarcheInfos.effectif"
                                    type="number"
                                    min="0"
                                    :disabled="isEntrepriseMarcheInfosDisabled || (isDMDInfo && effectifInfolegale)"
                                    required>
                                </b-form-input>
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <b-form-group
                                id="input-group-anneeChiffreAffaire"
                                label-for="input-anneeChiffreAffaire">
                                <template v-slot:label>
                                    <label-custom
                                        :label=empreinteEco.libelleAnneeCA
                                        :tooltip=empreinteEco.infoBulleAnneeCA
                                        :required="!chiffreAffaireInfolegale">
                                    </label-custom>
                                </template>
                                <b-form-select
                                    id="input-anneeChiffreAffaire"
                                    v-model="empreinte.entrepriseMarcheInfos.anneeChiffreAffaire"
                                    :options="annees"
                                    :disabled="isEntrepriseMarcheInfosDisabled || (isDMDInfo && chiffreAffaireInfolegale)"
                                    required>
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group
                                id="input-group-anneeEffectif"
                                label-for="input-anneeEffectif">
                                <template v-slot:label>
                                    <label-custom
                                        :label=empreinteEco.libelleAnneeEffectif
                                        :tooltip=empreinteEco.infoBulleAnneeEffectif
                                        :required="!effectifInfolegale">
                                    </label-custom>
                                </template>
                                <b-form-select
                                    id="input-anneeEffectif"
                                    v-model="empreinte.entrepriseMarcheInfos.anneeEffectif"
                                    :options="annees"
                                    :disabled="isEntrepriseMarcheInfosDisabled || (isDMDInfo && effectifInfolegale)"
                                    required>
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <!--                    US 471 -->
                    <b-row>
                        <b-col>
                            <b-form-group
                                id="input-group-bilanTotal"
                                label-for="input-bilanTotal">
                                <template v-slot:label>
                                    <label-custom
                                        :label=empreinteEco.bilanTotal
                                        :tooltip=empreinteEco.infoBulleBilanTotal
                                        :required="false">
                                    </label-custom>
                                </template>
                                <b-form-input
                                    id="input-bilanTotal"
                                    v-model="empreinte.entrepriseMarcheInfos.bilanTotal"
                                    type="number"
                                    min="0"
                                    step="any"
                                    :disabled="isEntrepriseMarcheInfosDisabled || (isDMDInfo && bilanInfolegale)"
                                    required>
                                </b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group
                                id="input-group-autonome"
                                label-for="input-autonome">
                                <template v-slot:label>
                                    <label-custom
                                        :label=empreinteEco.autonome
                                        :tooltip=empreinteEco.infoBulleBilanAutonome
                                        :required="false">
                                    </label-custom>
                                </template>
                                <b-form-radio-group
                                    id="input-dc4"
                                    v-model="empreinte.entrepriseMarcheInfos.autonome"
                                    :buttons="true"
                                    button-variant="outline-primary"
                                    required
                                    :disabled="isAutonomyPMEDisabled">
                                    <b-form-radio :value="true">Oui</b-form-radio>
                                    <b-form-radio :value="false">Non</b-form-radio>
                                </b-form-radio-group>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col cols="4">
                            <b-form-group
                                id="input-group-codeNaf"
                                label-for="input-codeNaf">
                                <template v-slot:label>
                                    <label-custom
                                        :label=empreinteEco.codeNaf
                                        :tooltip=empreinteEco.codeNaf>
                                    </label-custom>
                                </template>
                                <b-form-input
                                    id="input-codeNaf"
                                    v-model="empreinte.entrepriseMarcheInfos.codeNaf"
                                    :disabled="true">
                                </b-form-input>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-row class="justify-content-center">
                        <b-button
                            v-if="isAdminEmpreinte && entrepriseMarcheInfosFound && !empreinte.entrepriseMarcheInfos.sourceApiDate"
                            type="button"
                            variant="outline-primary"
                            class="mt-1"
                            @click="onEditInfosClicked">
                            Modifier les données de qualification
                        </b-button>
                    </b-row>
                    <b-row class="mb-6" v-if="(isDMDInfo && bilanInfolegale) || isBilanInfolegaleVisible">
                        <b-col cols="4">
                            <b-alert show variant="warning">{{ empreinteEco.bilanInfolegale }}</b-alert>
                        </b-col>
                    </b-row>
                    <b-row class="mb-6"
                           v-if="(isDMDInfo && chiffreAffaireInfolegale) || isChiffreAffaireInfolegaleVisible">
                        <b-col cols="4">
                            <b-alert show variant="warning">{{ empreinteEco.chiffreAffaireInfolegale }}</b-alert>
                        </b-col>
                    </b-row>
                    <b-row class="mb-6" v-if="(isDMDInfo && effectifInfolegale) || isEffectifInfolegaleVisible">
                        <b-col cols="4">
                            <b-alert show variant="warning">{{ empreinteEco.effectifInfolegale }}</b-alert>
                        </b-col>
                    </b-row>
                </b-card>

                <b-card header="Données de contractualisation">
                    <b-row>
                        <b-col cols="3">
                            <b-form-group id="input-group-nature" label-for="input-nature">
                                <template v-slot:label>
                                    <label-custom
                                        :label=empreinteEco.libelleNature
                                        :tooltip=empreinteEco.infoBulleNature
                                        :required="true">
                                    </label-custom>
                                </template>
                                <b-form-select
                                    id="input-nature"
                                    v-model="empreinte.nature"
                                    :options="natures"
                                    @change="onNatureChange"
                                    :disabled="mode === 'READ' || (mode === 'UPDATE' && !isAdminEmpreinte)"
                                    required>
                                </b-form-select>
                            </b-form-group>
                        </b-col>

                        <b-col cols="6"
                               v-if="['SOUS_TRAITANT', 'FOURNISSEUR', 'PRESTATAIRE'].includes(empreinte.nature)">
                            <b-form-group
                                id="input-group-sousTraitantId"
                                :label=empreinteEco.libelleSousTraitantId
                                label-for="input-sousTraitantId"
                                label-class="typo-required-field">
                                <AutocompleteCustom
                                    id="input-sousTraitantId"
                                    :items="entreprisesSousTraitant"
                                    v-model="empreinte.sousTraitantId"
                                    @search="onSiretLibelleSearch($event, true)"
                                    :required="true"
                                    :disabled="mode === 'READ' || (mode === 'UPDATE' && !isAdminEmpreinte)"
                                    :formatter="formatSiretSiren"
                                    :extra-option=empreinteEco.libelleNouvelleEntreprise
                                    @extraOptionClicked="onExtraOptionClicked(true)">
                                </AutocompleteCustom>
                            </b-form-group>
                        </b-col>
                        <b-col cols="3"
                               v-if="['SOUS_TRAITANT', 'FOURNISSEUR', 'PRESTATAIRE'].includes(empreinte.nature)">
                            <b-form-group
                                id="input-group-rang-sous-traitant"
                                :label=empreinteEco.libelleRangSousTraitant
                                label-for="input-rang-sous-traitant"
                                label-class="typo-required-field">
                                <b-form-select
                                    id="input-rang-sous-traitant"
                                    v-model="empreinte.rangSousTraitant"
                                    :options="rangs"
                                    required
                                    :disabled="mode === 'READ'">
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col>
                            <b-form-group
                                id="input-group-objetPrestation"
                                label-for="input-objetPrestation">
                                <template v-slot:label>
                                    <label-custom
                                        :label=empreinteEco.libelleObjetPrestation
                                        :tooltip=empreinteEco.infoBulleObjetPrestation
                                        :required="true">
                                    </label-custom>
                                </template>
                                <b-form-input
                                    id="input-objetPrestation"
                                    v-model="empreinte.objetPrestation"
                                    type="text"
                                    maxlength="400"
                                    :trim="true"
                                    required
                                    :disabled="mode === 'READ' || (mode === 'UPDATE' && !isAdminEmpreinte)"
                                ></b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group
                                id="input-group-categoriePrestation"
                                label-for="input-categoriePrestation">
                                <template v-slot:label>
                                    <label-custom
                                        :label=empreinteEco.libelleCategoriePrestation
                                        :tooltip=empreinteEco.infoBulleCategoriePrestation
                                        :required="true">
                                    </label-custom>
                                </template>
                                <b-form-select
                                    id="input-categoriePrestation"
                                    v-model="empreinte.categoriePrestation"
                                    :options="categoriesSecteur"
                                    required
                                    :disabled="mode === 'READ'"
                                ></b-form-select>
                            </b-form-group>
                            <b-form-group
                                v-if="empreinte.categoriePrestation && empreinte.categoriePrestation.libelle === 'AUTRE'"
                                id="input-group-categoriePrestationAutre"
                                :label=empreinteEco.libelleCategoriePrestationAutre
                                label-for="input-categoriePrestationAutre"
                                label-class="typo-required-field">
                                <b-form-input
                                    id="input-categoriePrestationAutre"
                                    v-model="empreinte.categoriePrestationAutre"
                                    type="text"
                                    maxlength="100"
                                    :trim="true"
                                    :required="true"
                                    :disabled="mode === 'READ'">
                                </b-form-input>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col>
                            <b-form-group
                                id="input-group-datePremiereCommande"
                                label-for="input-datePremiereCommande">
                                <template v-slot:label>
                                    <label-custom
                                        :label=empreinteEco.libelleDatePremiereCommande
                                        :tooltip=empreinteEco.infoBulleDatePremiereCommande
                                        :required="true">
                                    </label-custom>
                                </template>
                                <DatepickerCustom
                                    v-model="empreinte.datePremiereCommande"
                                    id="input-datePremiereCommande"
                                    :state="getDatepickerState"
                                    :disabled="mode === 'READ' || (mode === 'UPDATE' && !isAdminEmpreinte)">
                                </DatepickerCustom>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group id="input-group-dc4" label-for="input-dc4">
                                <template v-slot:label>
                                    <label-custom
                                        :label=empreinteEco.libelleDC4
                                        :tooltip=empreinteEco.infoBulleDC4
                                        :required="true">
                                    </label-custom>
                                </template>
                                <b-form-radio-group
                                    id="input-dc4"
                                    v-model="empreinte.dc4"
                                    :buttons="true"
                                    button-variant="outline-primary"
                                    required
                                    :disabled="mode === 'READ'">
                                    <b-form-radio :value="true">Oui</b-form-radio>
                                    <b-form-radio :value="false">Non</b-form-radio>
                                </b-form-radio-group>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col>
                            <b-form-group
                                id="input-group-montantMarche"
                                label-for="input-montantMarche">
                                <template v-slot:label>
                                    <label-custom
                                        :label=empreinteEco.libelleMontantMarche
                                        :tooltip=empreinteEco.infoBulleMontantMarche
                                        :required="true">
                                    </label-custom>
                                </template>
                                <b-form-input
                                    id="input-montantMarche"
                                    v-model="empreinte.montantMarche"
                                    type="number"
                                    step="any"
                                    required
                                    :disabled="mode === 'READ'"
                                    @change="updateMontantPme(empreinte.entrepriseMarcheInfos.pme)"
                                ></b-form-input>
                            </b-form-group>
                            <b-form-group
                                v-if="montants.length > 0"
                                id="input-group-montantMarcheM1"
                                label-for="input-montantMarcheM1">
                                <template v-slot:label>
                                    <label-custom
                                        :label="`Montant versé à date M - ${nbMonthSinceMontant1}`"></label-custom>
                                </template>
                                <b-form-input
                                    id="input-montantMarcheM1"
                                    :value="montants[0].montant"
                                    type="number"
                                    disabled
                                ></b-form-input>
                            </b-form-group>
                            <b-form-group
                                v-if="montants.length > 1"
                                id="input-group-montantMarcheM2"
                                label-for="input-montantMarcheM2">
                                <template v-slot:label>
                                    <label-custom
                                        :label="`Montant versé à date M - ${nbMonthSinceMontant2}`"></label-custom>
                                </template>
                                <b-form-input
                                    id="input-montantMarcheM2"
                                    :value="montants[1].montant"
                                    type="number"
                                    disabled
                                ></b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group
                                id="input-group-montantPmeEntreprise"
                                label-for="input-montantPmeEntreprise">
                                <template v-slot:label>
                                    <label-custom
                                        :label=empreinteEco.libelleMontantPmeEntreprise
                                        :tooltip=empreinteEco.infoBulleMontantPmeEntreprise
                                        :required="true">
                                    </label-custom>
                                </template>
                                <b-form-input
                                    id="input-montantPmeEntreprise"
                                    v-model="empreinte.montantPmeEntreprise"
                                    type="number"
                                    step="any"
                                    required
                                    :disabled="true">
                                </b-form-input>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-card>

                <b-row class="pt-2">
                    <b-col cols="6">
                        <b-form-group
                            id="input-group-numeroIdentification"
                            label-for="input-numeroIdentification"
                        >
                            <template v-slot:label>
                                <label-custom
                                    :label=empreinteEco.libelleNumeroIdentification
                                    :tooltip=empreinteEco.infoBulleNumeroIdentification
                                >
                                </label-custom>
                            </template>
                            <b-form-textarea
                                id="input-numero-identification"
                                v-model="empreinte.numeroIdentification"
                                rows="3"
                                max-rows="5"
                                maxlength="255"
                                :disabled="mode === 'READ'">
                            </b-form-textarea>
                        </b-form-group>
                    </b-col>
                    <b-col cols="6" v-if="mode === 'CREATE'">
                        <b-form-group
                            id="input-group-fichier-justificatif"
                            :label="libelles.fichierJustificatif"
                            label-for="input-fichier-justificatif"
                        >
                            <template v-slot:label>
                                <label-custom
                                    :label="empreinteEco.libellePieceJustificative"
                                    :tooltip="empreinteEco.infoBullePieceJustificative"
                                >
                                </label-custom>
                            </template>
                            <b-form-file
                                v-model="empreinte.pieceJustificative"
                                accept=".pdf"
                                drop-placeholder="Déposer le fichier ici"
                                placeholder="Choisir une pièce justificative"
                                browse-text="Importer une pièce justificative"
                            ></b-form-file>
                        </b-form-group>

                    </b-col>
                    <b-col cols="6" v-if="mode !== 'CREATE'">
                        <b-form-group
                            id="input-group-commentaire"
                            :label=libelles.commentaire
                            label-for="input-commentaire"
                            :label-class="empreinte.jourSoumissionPassed ? 'typo-required-field' : ''">
                            <b-form-textarea
                                id="input-commentaire"
                                v-model="empreinte.commentaire"
                                rows="3"
                                max-rows="5"
                                :required="empreinte.jourSoumissionPassed"
                                :disabled="mode === 'READ'"></b-form-textarea>
                        </b-form-group>
                    </b-col>
                    <b-col cols="6" v-if="empreinte.statut === 'REFUSE'">
                        <b-form-group
                            id="input-group-commentaire-statut"
                            :label=libelles.commentaireStatut
                            label-for="input-commentaire-statut"
                        >
                            <b-form-textarea
                                id="input-commentaire"
                                :value="empreinte.commentaireStatut"
                                rows="3"
                                max-rows="5"
                                disabled
                            ></b-form-textarea>
                        </b-form-group>
                    </b-col>
                </b-row>

                <div class="pt-2" v-if="mode !== 'READ'">
                    <b-button type="submit"
                              variant="primary"
                              @click="formSubmitted = true">
                        {{ libelles.soumettre }}
                    </b-button>
                    <b-button v-if="mode === 'CREATE'" type="reset" variant="danger">
                        {{ libelles.reinitialiser }}
                    </b-button>
                </div>

                <div class="pt-2" v-if="mode === 'READ' && isAdminEmpreinte">
                    <b-button
                        v-if="empreinte.statut === 'ATTENTE_MODIFICATION' || empreinte.statut === 'ATTENTE_SUPPRESSION'"
                        type="button"
                        variant="primary"
                        @click="validerEmpreinte(empreinte)">
                        <span v-if="empreinte.statut === 'ATTENTE_MODIFICATION'">
                            {{ libelles.confirmerModification }}
                        </span>
                        <span v-if="empreinte.statut === 'ATTENTE_SUPPRESSION'">
                            {{ libelles.confirmerSuppression }}</span>
                    </b-button>
                </div>
            </b-col>
        </b-form>
        <br/>
    </div>
</template>

<script>
import EmpreinteEconomiqueStore from "@/stores/EmpreinteEconomiqueStore";
import EntrepriseStore from "@/stores/EntrepriseStore";
import AlertStore from "@/stores/AlertStore";
import SaveFormEntrepriseStore from "../../stores/SaveFormEntrepriseStore";
import SaveFormEntrepriseMarcheInfosStore from "../../stores/SaveFormEntrepriseMarcheInfosStore";
import {NatureEntrepriseEnum} from "../../constants/enums";
import EmpreinteEconomiqueDto from "../../dtos/EmpreinteEconomiqueDto";
import EntrepriseMarcheInfosStore from "../../stores/EntrepriseMarcheInfosStore";
import AutocompleteCustom from "../../components/AutocompleteCustom";
import LabelCustom from "../../components/LabelCustom";
import DatepickerCustom from "../../components/DatepickerCustom";
import SessionStore from "@/stores/SessionStore";
import CategorieSecteurStore from "../../stores/CategorieSecteurStore";
import formatSiretSiren from "../../filters/formatSiretSiren";
import hasPermission from "../../filters/hasPermission";
import MarcheStore from "../../stores/MarcheStore";
import {EventBus} from "@/event-bus";
import moment from "moment";
import {mois} from "../../constants/enums";
import {momentToDate, nbMonthsBetweenDates} from "../../utils/dateUtil";
import {DEPARTEMENTS_IDF} from "../../constants/common";
import _ from 'lodash';
import {libelles, empreinteEco} from '../../constants/libelles';
import DispenseReportingDto from "../../dtos/DispenseReportingDto";
import ReportingStore from "../../stores/ReportingStore";
import EntrepriseMarcheInfosDto from "../../dtos/EntrepriseMarcheInfosDto";
import MonthlyPicker from "@/components/MonthlyPicker.vue";

export default {
    components: {
        MonthlyPicker,
        LabelCustom,
        AutocompleteCustom,
        DatepickerCustom,
    },
    data() {
        return {
            formSubmitted: false,
            show: true,
            showCreateEntrepriseButton: false,
            sessionStoreState: SessionStore.state,
            empreinte: new EmpreinteEconomiqueDto(),
            entrepriseMarcheInfosFound: false,
            entrepriseId: null,
            entrepriseEss: false,
            marcheAdmin: null,
            marches: [],
            marcheIds: [],
            entreprisesSiret: [],
            entreprisesSousTraitant: [],
            annees: [],
            rangs: [1, 2, 3],
            natures: Object.entries(NatureEntrepriseEnum).map((value) => {
                return {text: value[1], value: value[0]};
            }),
            categoriesSecteur: [],
            minDate: moment().subtract(3, 'months'),
            maxDate: moment(),
            date: null,
            monthLabels: mois,
            donneesPreRemplies: false,
            libelles: libelles,
            empreinteEco: empreinteEco,
            marcheIdsItems: [],
            montants: [],
            isDMDInfo: false,
            effectifInfolegale: false,
            chiffreAffaireInfolegale: false,
            bilanInfolegale: false,
            isPourcentagePlus: false,
            isInfoFournisseur: false,
            pmeInfolegale: false
        };
    },
    computed: {
        user() {
            if (this.mode === "CREATE") {
                return {
                    email: SessionStore.state.email,
                    prenom: SessionStore.state.prenom,
                    nom: SessionStore.state.nom
                }
            }
            return {
                email: this.empreinte.user?.email || " ",
                prenom: this.empreinte.user?.prenom || " ",
                nom: this.empreinte.user?.nom || " "
            }
        },

        mode() {
            return !this.$route.params.id ? "CREATE" : (this.empreinte.statut === "VALIDE" && !hasPermission(["CORRESPONDANT_EMPREINTE_LECTURE"])) ? "UPDATE" : "READ";
        },
        isAdminEmpreinte() {
            return hasPermission(["ADMINISTRATEUR", "CORRESPONDANT_EMPREINTE_ADMINISTRATEUR"]);
        },
        isAdmin() {
            return this.isAdminEmpreinte && !this.sessionStoreState.marches.length;
        },
        entreprise() {
            let entrepriseFound;
            if (this.entrepriseId &&
                this.entreprisesSiret &&
                this.entreprisesSiret.length > 0) {
                entrepriseFound = this.entreprisesSiret.find(
                    (value) => value.value === this.entrepriseId
                );
            }
            return entrepriseFound ? entrepriseFound.entreprise : null;
        },
        marcheId() {
            return this.mode === "CREATE" ? this.isAdmin ? this.marcheAdmin : this.sessionStoreState.marche.id : this.empreinte.entrepriseMarcheInfos.marcheId;
        },
        isEntrepriseMarcheInfosDisabled() {
            return this.mode === "CREATE" ? this.entrepriseMarcheInfosFound || !this.entrepriseId : true;
        },
        isAutonomyPMEDisabled() {
            return this.mode === "UPDATE" || this.mode === "READ" || (this.mode === "CREATE" && this.isEntrepriseMarcheInfosDisabled);
        },
        getDatepickerState() {
            return this.formSubmitted ? !!this.empreinte.datePremiereCommande : null;
        },
        isDateFilled() {
            return this.formSubmitted ? this.date != null && this.date._isValid : true;
        },
        computedEmpreinte() {
            return Object.assign({}, this.empreinte);
        },
        marche() {
            return this.isAdmin ? this.marches.find(marche => marche.id === this.marcheAdmin) : this.sessionStoreState.marche;
        },
        isBilanInfolegaleVisible() {
            return (this.mode === "READ" || this.mode === "UPDATE") && this.empreinte.entrepriseMarcheInfos.bilanInfolegale;
        },
        isEffectifInfolegaleVisible() {
            return (this.mode === "READ" || this.mode === "UPDATE") && this.empreinte.entrepriseMarcheInfos.effectifInfolegale;
        },
        isChiffreAffaireInfolegaleVisible() {
            return (this.mode === "READ" || this.mode === "UPDATE") && this.empreinte.entrepriseMarcheInfos.chiffreAffaireInfolegale;
        },
        nbMonthSinceMontant1() {
            return this.date && this.montants.length > 0 ? nbMonthsBetweenDates(this.date, this.montants[0].date) : 1
        },
        nbMonthSinceMontant2() {
            return this.date && this.montants.length > 1 ? nbMonthsBetweenDates(this.date, this.montants[1].date) : 2
        }
    },
    watch: {
        $route() {
            this.initEmpreinte();
        },
        entrepriseId() {
            this.onEntrepriseMarcheChange();
            this.onEntrepriseMarcheDateChange()
        },
        date() {
            this.onEntrepriseMarcheDateChange()
        },
        computedEmpreinte: {
            handler(newVal, oldVal) {
                const ignoreDatePremiereCommande = oldVal.datePremiereCommande != null && typeof oldVal.datePremiereCommande === 'string';
                const dateChanged = oldVal.date !== newVal.date;
                const montantPmeChanged = oldVal.montantPmeEntreprise !== newVal.montantPmeEntreprise;
                if (!_.isEqual(oldVal, newVal) && oldVal.montantMarche === newVal.montantMarche && !ignoreDatePremiereCommande && !dateChanged && !montantPmeChanged) {
                    if (this.donneesPreRemplies) {
                        this.empreinte.id = null;
                    }
                    this.donneesPreRemplies = false;
                }
            },
            deep: true
        },
        marcheAdmin(newMarcheAdmin) {
            this.onMarcheSearch(newMarcheAdmin, true);
        },
    },
    mounted() {
        CategorieSecteurStore.getAll().then((response) => {
            this.categoriesSecteur = response.map((value) => {
                return {text: value.libelle, value: value};
            });
        });
        const currentYear = new Date().getFullYear();
        for (let i = currentYear; i >= 2010; i--) {
            this.annees.push(i);
        }
        if (this.isAdmin) {
            MarcheStore.getAll().then((marches) => {
                this.marches = marches;
                this.marcheIds = marches.map((marche) => {
                    return {text: `${marche.reference} (${marche.libelleCourt})`, value: marche.id};
                });
                this.marcheIdsItems = this.marcheIds;
            });
        }
        EventBus.$on("NavBar-marcheChange", () => {
            if (!this.isAdmin) {
                this.initEmpreinte();
            }
        });
        this.initEmpreinte();
    },
    beforeDestroy() {
        EventBus.$off("NavBar-marcheChange");
    },
    methods: {
        initEmpreinte() {
            this.entrepriseId = null;
            this.marcheAdmin = null;
            this.date = null;
            this.entrepriseMarcheInfosFound = false;
            this.formSubmitted = false;
            this.show = false;
            this.showCreateEntrepriseButton = false;
            this.$nextTick(() => {
                this.show = true;
            });
            if (this.mode !== "CREATE") {
                EmpreinteEconomiqueStore.get(this.$route.params.id).then((empreinte) => {
                    if (empreinte) {
                        this.setEmpreinteMarcheDateSirets(empreinte, empreinte.entrepriseMarcheInfos.marcheId, empreinte.date, empreinte.entrepriseMarcheInfos.entrepriseId, empreinte.sousTraitantId);
                    }
                });
            } else {
                if (SaveFormEntrepriseStore.state.active) {
                    this.restoreFormEntreprise();
                } else if (SaveFormEntrepriseMarcheInfosStore.state.active) {
                    this.setEmpreinteMarcheDateSirets(
                        SaveFormEntrepriseMarcheInfosStore.state.form.empreinte,
                        SaveFormEntrepriseMarcheInfosStore.state.form.marcheAdmin,
                        SaveFormEntrepriseMarcheInfosStore.state.form.date,
                        SaveFormEntrepriseMarcheInfosStore.state.form.entrepriseId);
                    SaveFormEntrepriseMarcheInfosStore.clear();
                } else {
                    this.empreinte = new EmpreinteEconomiqueDto();
                }
            }
        },
        restoreFormEntreprise() {
            this.setEmpreinteMarcheDateSirets(
                SaveFormEntrepriseStore.state.form.empreinte,
                SaveFormEntrepriseStore.state.form.marcheAdmin,
                SaveFormEntrepriseStore.state.form.date,
                SaveFormEntrepriseStore.state.form.entrepriseId,
                SaveFormEntrepriseStore.state.form.empreinte.sousTraitantId);
            const entreprise = SaveFormEntrepriseStore.state.data;
            if (SaveFormEntrepriseStore.state.form.forSousTraitant) {
                this.entreprisesSousTraitant = EntrepriseStore.getEntrepriseAutocomplete(entreprise);
                this.empreinte.sousTraitantId = entreprise.id;
            } else {
                this.entreprisesSiret = EntrepriseStore.getEntrepriseAutocomplete(entreprise);
                this.entrepriseId = entreprise.id;
            }
            SaveFormEntrepriseStore.clear();
        },
        onEntrepriseMarcheChange() {
            if (this.mode === "CREATE") {
                if (this.entrepriseId && this.marcheId && !SaveFormEntrepriseStore.state.active) {
                    EmpreinteEconomiqueStore.getLatestByEntrepriseIdAndMarcheId(this.entrepriseId, this.marcheId).then((empreinte) => {
                        if (empreinte) {
                            AlertStore.pushWarning(`Attention, le cadre "données de contractualisation" a été pré-rempli avec les données relatives à cette entreprise. Merci de bien vérifier les champs de ce cadre et de les modifier si nécessaire.`);
                            this.setEmpreinteMarcheDateSirets(empreinte, empreinte.entrepriseMarcheInfos.marcheId, null, empreinte.entrepriseMarcheInfos.entrepriseId, empreinte.sousTraitantId);
                            this.entrepriseMarcheInfosFound = true;
                            this.donneesPreRemplies = true;
                            this.empreinte.createdDate = null;
                            this.updateQualifFournisseurMDM();
                        } else {
                            this.empreinte = new EmpreinteEconomiqueDto();
                            EntrepriseMarcheInfosStore.getByEntrepriseIdAndMarcheId(this.entrepriseId, this.marcheId).then((infos) => {
                                this.initEntrepriseMarcheInfos(infos);
                            });
                        }
                    });
                } else {
                    this.initEntrepriseMarcheInfos();
                }
            }
        },
        onEntrepriseMarcheDateChange() {
            if (this.entrepriseId && this.marcheId && this.date) {
                EmpreinteEconomiqueStore.getMontants(this.entrepriseId, this.marcheId, this.date).then((montants) => {
                    this.montants = montants.lastMonths.sort((a, b) => moment(b.date) - moment(a.date))
                })
            }
        },
        initEntrepriseMarcheInfos(infos) {
            this.entrepriseMarcheInfosFound = !!infos;
            if (infos) {
                this.empreinte.entrepriseMarcheInfos = infos;
            } else if (!SaveFormEntrepriseStore.state.active || !SaveFormEntrepriseStore.state.form.forSousTraitant) {
                this.empreinte.entrepriseMarcheInfos = new EntrepriseMarcheInfosDto();
                this.empreinte.entrepriseMarcheInfos.entrepriseId = this.entrepriseId;
                this.empreinte.entrepriseMarcheInfos.marcheId = this.marcheId;
                this.empreinte.entrepriseMarcheInfos.francilienne = (this.entreprise && this.entreprise.villeSiren) ?
                    DEPARTEMENTS_IDF.includes(this.entreprise.villeSiren.departement.code) : false;
                this.empreinte.entrepriseMarcheInfos.agenceIdf = (this.entreprise && this.entreprise.villeSiret) ?
                    DEPARTEMENTS_IDF.includes(this.entreprise.villeSiret.departement.code) : false;
            }
        },
        setEmpreinteMarcheDateSirets(empreinte, marcheId, date, entrepriseId, sousTraitantId) {
            this.empreinte = empreinte;
            this.marcheAdmin = marcheId;
            if (date) {
                this.date = moment(date);
            }
            if (entrepriseId) {
                this.entrepriseId = entrepriseId;
                this.setEntreprisesSiret(this.entrepriseId);
            }
            if (sousTraitantId) {
                this.setEntreprisesSousTraitant(sousTraitantId);
            }
        },
        setEntreprisesSiret(entrepriseId) {
            EntrepriseStore.get(entrepriseId).then((entreprise) => {
                this.entreprisesSiret = EntrepriseStore.getEntrepriseAutocomplete(entreprise);
            });
        },
        setEntreprisesSousTraitant(sousTraitantId) {
            EntrepriseStore.get(sousTraitantId).then((sousTraitant) => {
                this.entreprisesSousTraitant = EntrepriseStore.getEntrepriseAutocomplete(sousTraitant);
            });
        },
        formatSiretSiren(value) {
            return formatSiretSiren(value);
        },
        async onSiretLibelleSearch(siret, isSousTraitant) {
            if (siret && this.marcheId) {
                if (isSousTraitant) {
                    this.entreprisesSousTraitant = await EntrepriseStore.getBySiretOrLibelle(siret, false, this.marcheId, false, false);
                } else {
                    this.entreprisesSiret = await EntrepriseStore.getBySiretOrLibelle(siret, true, this.marcheId, true, false);
                }
            }
        },
        onExtraOptionClicked(forSousTraitant) {
            SaveFormEntrepriseStore.state.active = true;
            SaveFormEntrepriseStore.state.route = this.$route;
            SaveFormEntrepriseStore.state.form = {
                empreinte: this.empreinte,
                entrepriseId: this.entrepriseId,
                marcheId: this.marcheId,
                marcheAdmin: this.marcheAdmin,
                date: this.date,
                forSousTraitant,
            };
            window.scrollTo(0, 0);
            this.$router.push("/entreprises/creation");
        },
        onEditInfosClicked() {
            SaveFormEntrepriseMarcheInfosStore.state.active = true;
            SaveFormEntrepriseMarcheInfosStore.state.route = this.$route;
            SaveFormEntrepriseMarcheInfosStore.state.form = {
                empreinte: this.empreinte,
                entrepriseId: this.entrepriseId,
                marcheId: this.marcheId,
                marcheAdmin: this.marcheAdmin,
                date: this.date,
            };
            this.$router.push("/marches/entreprise-marche-infos");
        },
        onNatureChange() {
            this.empreinte.sousTraitantId = null;
            this.empreinte.rangSousTraitant = null;
        },
        onSubmit(evt) {
            evt.preventDefault();
            if (!this.entrepriseId) {
                AlertStore.pushError("L'entreprise renseignée n'existe pas dans le référentiel entreprises, merci de sélectionner une entreprise dans la liste déroulante ou d'en créer une nouvelle.");
                this.showCreateEntrepriseButton = true;
                const el = document.getElementById("input-group-entrepriseId");
                el.scrollIntoView();
                return;
            }
            const sousTraitantNeeded = ['SOUS_TRAITANT', 'FOURNISSEUR', 'PRESTATAIRE'].includes(this.empreinte.nature);
            if (sousTraitantNeeded && !this.empreinte.sousTraitantId) {
                AlertStore.pushError('Merci de cliquer sur le SIRET commanditaire dans la liste déroulante');
                return;
            }
            if (!this.isDateFilled) {
                AlertStore.pushError('Mois non renseigné');
                return;
            }
            if (this.getDatepickerState) {
                this.empreinte.date = momentToDate(this.date);
                if (this.mode === "UPDATE" || this.donneesPreRemplies) {
                    EmpreinteEconomiqueStore.update(this.empreinte);
                } else {
                    let selectedEntreprise = null;
                    this.entreprisesSiret.forEach(ent => {
                        if (ent.entreprise.id === this.entrepriseId) {
                            selectedEntreprise = ent.entreprise;
                        }
                    })
                    this.empreinte.mdm = selectedEntreprise != null ? selectedEntreprise.mdm : false;
                    this.empreinte.entrepriseMarcheInfos.bilanInfolegale = this.bilanInfolegale;
                    this.empreinte.entrepriseMarcheInfos.chiffreAffaireInfolegale = this.chiffreAffaireInfolegale;
                    this.empreinte.entrepriseMarcheInfos.effectifInfolegale = this.effectifInfolegale;
                    EmpreinteEconomiqueStore.create(this.empreinte);
                }
            }
        },
        onReset(evt) {
            evt.preventDefault();
            SaveFormEntrepriseStore.clear();
            SaveFormEntrepriseMarcheInfosStore.clear();
            this.initEmpreinte();
        },
        validerEmpreinte(empreinte) {
            EmpreinteEconomiqueStore.validate(empreinte).then(() => {
                this.$router.push("/empreinte-economique/validation");
            });
        },
        updateMontantPme(isPme) {
            this.empreinte.montantPmeEntreprise = isPme ? this.empreinte.montantMarche : 0;
        },
        saveDispense() {
            const dispense = new DispenseReportingDto();
            dispense.marche = this.marche;
            dispense.ongletReporting = "EMPREINTE";
            dispense.dateDebut = momentToDate(this.date);
            dispense.dateFin = momentToDate(this.date);
            ReportingStore.create(dispense, true);
        },
        onMarcheSearch(marche, isId) {
            this.marcheIdsItems = MarcheStore.onMarcheSearch(marche, isId, this.marcheIds)
        },
        isBeforeCurrentDate() {
            var currentdate = new Date();
            if (this.date) {
                return momentToDate(this.date) < currentdate;
            }
        },
        updateQualifFournisseurMDM() {
            if (this.entrepriseId && this.marcheId) {
                EntrepriseStore.getInfoFournisseurBySiret(this.entrepriseId, this.marcheId).then((infoFournisseur) => {
                    if (infoFournisseur && this.mode === "CREATE") {
                        this.empreinte = {
                            ...this.empreinte,
                            entrepriseMarcheInfos: {
                                ...this.empreinte.entrepriseMarcheInfos,
                                chiffreAffaire: parseInt(infoFournisseur.chiffreAffaire),
                                effectif: parseInt(infoFournisseur.effectif),
                                sourceApiDate: infoFournisseur.sourceApiDate,
                                bilanTotal: infoFournisseur.bilanTotal,
                                codeNaf: infoFournisseur.codeNaf,
                                pme: infoFournisseur.isPme !== null ? infoFournisseur.isPme : this.empreinte.entrepriseMarcheInfos.pme
                            }
                        }

                        if (infoFournisseur.effectifInfolegale) {
                            this.effectifInfolegale = true;
                        }

                        if (infoFournisseur.chiffreAffaireInfolegale) {
                            this.chiffreAffaireInfolegale = true;
                        }

                        if (infoFournisseur.bilanInfolegale) {
                            this.bilanInfolegale = true;
                        }

                        if (infoFournisseur.isPme !== null) {
                            this.pmeInfolegale = true;
                        }

                        this.isPourcentagePlus = infoFournisseur.isPourcentagePlus;
                        this.isDMDInfo = true;
                        this.isInfoFournisseur = true;
                    }
                })
            }
        }
    },
};
</script>

<style scoped>
.infoDateValidation {
    font-size: 0.85em;
}

.user-info {
    background-color: #f0f0f0;
    padding: 10px;
    border: 1px solid #ccc;
    display: flex;
    flex-direction: column;
    max-width: 100%;
    box-sizing: border-box;
    word-break: break-word;
}

.no-data {
    text-align: center;
    font-style: italic;
    color: #888;
}
</style>
